import { useSelector, useDispatch } from "react-redux";
import {
  useGetContactUsListDataQuery,
  useUpdateTableFieldsDataMutation,
} from "../../../store/queries/contactUs";
import { updateConfig } from "../../../store/slices/ContactUsSlice/contactUsSlice";
import { useMemo, useState } from "react";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import Select from "react-select";
import { updateContactUsForm, exportGeneralEnquiry } from "./api";
import { toast } from "react-toastify";
import moment from "moment";

const useContactUs = () => {
  const dispatch = useDispatch();
  const contactUsState = useSelector((state) => state.contactUs);
  const {
    data: contactUsData = {},
    isFetching,
    isLoading,
    refetch,
  } = useGetContactUsListDataQuery({
    sort_by: contactUsState.sortBy,
    sort_order: contactUsState.sortOrder,
    search: contactUsState.search,
    page_size: contactUsState.currentPageSize,
    page: contactUsState.currentPage,
    filter: contactUsState.currentFilter,
  });

  const [statusModal, setStatusModal] = useState(false);
  const [statusReason, setStatusReason] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [errors, setErrors] = useState("");
  //this state is not required , but adding by considering the future changes
  const [selectedOption, setSelectedOption] = useState();

  const [updateTableFields] = useUpdateTableFieldsDataMutation();
  const { showEditModal } = useSelector((state) => state.global);

  const filterOptions = [
    {
      name: "All",
      value: "",
    },
    {
      name: "Open",
      value: 1,
    },
    {
      name: "Close",
      value: 2,
    },
  ];

  const OrderStatus = [
    {
      name: "Open",
      value: 1,
    },
    {
      name: "Close",
      value: 2,
    },
  ];

  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];

  const filters = useMemo(() => {
    if (filterOptions) {
      const buttonGroups = filterOptions?.filter((d) => !d?.type);
      return [{ type: "buttonGroup", buttons: buttonGroups }];
    } else {
      return [];
    }
    // eslint-disable-next-line
  }, []);

  const handleFilter = (filter) => {
    if (filter.value === null) {
      dispatch(
        updateConfig((state) => {
          state.currentFilter = filter.value;
          state.sortBy = "";
          state.sortOrder = "";
          state.currentPage = 1;
        }),
      );
      localStorage.setItem("currentFilter", filter?.value);
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentFilter = filter.value;
          state.currentPage = 1;
        }),
      );
      localStorage.setItem("currentFilter", filter.value);
    }
  };

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
      }),
    );
  };

  let actionOptions = [
    { label: "Edit Columns", value: 0 },
    { label: "Export", value: 1 },
  ];

  const handleExport = async () => {
    try {
      const response = await exportGeneralEnquiry({
        sort_by: contactUsState.sortBy,
        sort_order: contactUsState.sortOrder,
        search: contactUsState.search,
        filter: contactUsState.currentFilter,
      });

      const blob = new Blob([response?.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute(
        "download",
        `general_enquiry_${moment(Date.now()).format(
          "MM_DD_YYYY_HH_mm_ss",
        )}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toast.error("Failed to export. Please try again.");
    }
  };

  const handleActionChange = (item) => {
    if (item?.value === 0) {
      dispatch(
        globalUpdateConfig((state) => {
          state.showEditModal = true;
        }),
      );
    } else if (item?.value === 1) {
      handleExport();
    }
  };

  const handleClearClick = () => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = 1;
        state.currentPageSize = 10;
        state.sortBy = "";
        state.sortOrder = "";
        state.search = "";
        state.showCreateModal = false;
        state.showInvoiceModal = false;
        state.selctedId = "";
        state.is_active = null;
      }),
    );
  };

  const handleSort = (label) => {
    if (contactUsState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.sortOrder = contactUsState.sortOrder === "asc" ? "desc" : "asc";
        }),
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.sortBy = label;
          state.sortOrder = "asc";
        }),
      );
    }
  };

  const hasChangeStatusPermission = useMemo(() => {
    let permission = contactUsData?.data?.permission?.filter((p) =>
      Object.keys(p).includes("can_change_status"),
    );
    return permission?.[0]?.can_change_status ?? 0;
    // eslint-disable-next-line
  }, [contactUsData]);

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,

      status: (field, data) => {
        let selectVal = OrderStatus.find((item) => item.name === data[field]);

        return hasChangeStatusPermission ? (
          <Select
            id="status"
            name="status"
            placeholder={"Select"}
            className={`pro-input lg`}
            classNamePrefix="pro-input"
            options={OrderStatus}
            getOptionLabel={(option) => option?.name}
            getOptionValue={(option) => option?.value}
            value={selectVal}
            onChange={(value) => {
              if (value?.name === "Close") {
                setSelectedOption(value);
                setStatusModal(true);
                setSelectedItem(data);
              }
            }}
            menuPlacement="auto"
            menuPosition="fixed"
            isDisabled={selectVal?.value === 2}
          />
        ) : (
          <p>{data[field]}</p>
        );
      },
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
        state.clearSelection = true;
      }),
    );
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      }),
    );
  };

  const closeEditModal = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = false;
      }),
    );
  };

  const closeStatusReasonModal = () => {
    setStatusModal(false);
    setErrors("");
    setStatusReason("");
    setSelectedItem("");
    setSelectedOption("");
  };

  const handleSubmitStatusChange = () => {
    if (statusReason?.length <= 0) {
      setErrors("closing reason is required");
    } else {
      setErrors("");
      const obj = {
        contact_id: selectedItem?.id,
        status: selectedOption?.value,
        review_comment: statusReason,
      };

      updateContactUsForm(obj).then((res) => {
        if (res?.status === 200) {
          toast.success("Status changed successfully");
          refetch();
          setStatusModal(false);
          setErrors("");
          setStatusReason("");
          setSelectedItem("");
          setSelectedOption("");
        } else {
          toast.error("Failed to change status");
          setStatusModal(false);
          setErrors("");
          setStatusReason("");
          setSelectedItem("");
          setSelectedOption("");
        }
      });
    }
  };

  return {
    contactUsData,
    contactUsState,
    filters,
    activeFilter: contactUsState?.currentFilter,
    isLoading,
    actionOptions,
    isFetching,
    paginationOptions,
    showEditModal,
    statusModal,
    statusReason,
    errors,
    handleSubmitStatusChange,
    setStatusReason,
    closeStatusReasonModal,
    closeEditModal,
    refetch,
    updateTableFields,
    handlePageSize,
    handlePagination,
    getRow,
    handleSort,
    handleClearClick,
    handleActionChange,
    handleSearch,
    handleFilter,
  };
};

export default useContactUs;
